import React from 'react';

import cx from 'classnames';
import { isEmpty, path } from 'ramda';
import { FieldValues, useFormContext } from 'react-hook-form';

import TooltipDialog from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/ModalContents/TooltipDialog';
import Button from 'components/shared/Button';
import { EFlightStatus, ETripEvent, IApiFlightTripEvent } from 'detailsv2/structure';
import { useRelocationPoints } from 'hooks/useRelocationPointMutations/relocationPoints';

import cs from '../../index.module.css';
import { CustomSubmitHandler } from '../../types';
import { SaveInfoButton, SaveInfoTooltip, useTripEventDefaultValues } from '../shared';
import { FlightAPIOptions } from './FlightAPIOption/types';
import { FlightTab } from './types';

type Props = {
  activeTab: FlightTab;
  activeItems: FlightAPIOptions;
  tripEvent: IApiFlightTripEvent;
  onSubmit: CustomSubmitHandler<FieldValues>;
  isDisabled: boolean;
};

export const FlightButtonsGroup = (props: Props) => {
  const methods = useFormContext();

  const isEdit = !!props.tripEvent.id;
  const isManual = props.activeTab === FlightTab.MANUAL;
  const isNeedToShowTooltip =
    props.tripEvent.id && props.tripEvent.showChangedRoomOrPageTooltip && methods.formState?.dirtyFields?.attachedPage;

  const fieldValuesHandler = getFieldValuesHandler({
    isEdit,
    activeItems: props.activeItems,
    defaultFormValues: methods.formState.defaultValues,
  });

  const { withPointsResolve } = useRelocationPoints([
    methods.watch('data.arrivalTo'),
    methods.watch('data.departureFrom'),
  ]);

  const handleSubmitWithPointsResolve = withPointsResolve(props.onSubmit, [
    path(['data', 'arrivalTo']),
    path(['data', 'departureFrom']),
  ]);
  const manualSafeHandleSubmit = () => methods.handleSubmit(handleSubmitWithPointsResolve)();

  const submit = () => {
    if (isManual) {
      return manualSafeHandleSubmit();
    }

    const tripEventBase = methods.getValues();

    const formattedTripEvents = fieldValuesHandler(tripEventBase);
    props.onSubmit(formattedTripEvents);
  };

  const { isSafeInfoDisabled, safeInfoTooltipId, saveInfoSubmitHandler } = useTripEventDefaultValues({
    eventType: ETripEvent.Flight,
    methods,
    handleSubmit: props.onSubmit,
    isTripEventCreated: isEdit,
    fieldValuesHandler: isManual ? undefined : fieldValuesHandler,
  });

  return (
    <>
      <div className={cx(cs.footer, cs.improvedUIOnEventModalsFeature)}>
        <SaveInfoButton
          disabled={isSafeInfoDisabled || (!isManual && isEmpty(props.activeItems))}
          handleSaveForMyself={() => saveInfoSubmitHandler(false)}
          handleSaveForTeam={() => saveInfoSubmitHandler(true)}
          tooltipId={safeInfoTooltipId}
          isImprovedUIOnEventModalsFeature
        />

        <div className={cx(cs.rightFooter)}>
          {props.tripEvent.id && (
            <TooltipDialog tripEventId={props.tripEvent.id} onSubmit={() => {}}>
              <Button type="primary2" className={cs.buttonText} size="large" htmlType="button">
                Remove
              </Button>
            </TooltipDialog>
          )}

          {isNeedToShowTooltip ? (
            <TooltipDialog
              onSubmit={methods.handleSubmit(submit)}
              text={'Updating the Content Page here will overwrite \n the changes you had made within the itinerary.'}
              cancelButtonText="Cancel"
              confirmButtonText="Confirm"
            >
              <Button disabled={props.isDisabled} className={cs.buttonText} size="large" htmlType="button">
                Update
              </Button>
            </TooltipDialog>
          ) : (
            <Button
              onClick={methods.handleSubmit(submit)}
              disabled={props.isDisabled || (!isManual && isEmpty(props.activeItems))}
              className={cs.buttonText}
              size="large"
              htmlType="button"
            >
              {props.tripEvent.id ? 'Update' : 'Add To Itinerary'}
            </Button>
          )}
        </div>
      </div>

      <SaveInfoTooltip tooltipId={safeInfoTooltipId} />
    </>
  );
};

type GetFieldValuesHandler = {
  isEdit: boolean;
  activeItems: FlightAPIOptions;
  defaultFormValues?: FieldValues;
};

export const getFieldValuesHandler = ({ isEdit, activeItems, defaultFormValues }: GetFieldValuesHandler) => {
  const handleTripEvents = (tripEventBase: FieldValues) =>
    Object.values(activeItems).map((el) => {
      const {
        departureAirport,
        arrivalAirport,
        arrivalTime,
        departureTime,
        airline,
        flightNumber,
        flightStatus,
        newArrivalTime,
        newDepartureTime,
      } = el.data;
      const isFlightDateChanged =
        defaultFormValues?.data?.arrivalTime !== arrivalTime ||
        defaultFormValues?.data?.departureTime !== departureTime;

      return {
        ...tripEventBase,
        attachedPages: [tripEventBase.attachedPages, tripEventBase.attachedPage].filter(Boolean),
        data: {
          ...tripEventBase.data,
          departureAirport,
          arrivalAirport,
          arrivalTime,
          departureTime,
          airline,
          flightNumber,
          flightStatus,
          newArrivalTime,
          newDepartureTime,
          force_update_flight: isEdit && flightStatus === EFlightStatus.Common,
        },
        datesRange: {
          start: isFlightDateChanged ? tripEventBase?.data?.flightDate : tripEventBase?.datesRange?.start,
          end: el.datesRange.end,
        },
        daysRange: {
          ...tripEventBase.daysRange,
          end: el.daysRange.end,
        },
      };
    });

  return handleTripEvents;
};
